import React, {useEffect, useState, useRef, useCallback} from 'react';
import {View, Text, TouchableOpacity, Image, TextInput} from 'react-native';
import downArrow from '../../../assets/down_arrow.png';
import dropdownStyles from '../../style/dropdownStyles';
import usePersistentMenuState from '../../commons/usePersistentMenuState';
import i18n from 'i18n-js';

const ToggleButton = ({value, onToggle, style, testID}) => {
  return (
    <TouchableOpacity
      style={[dropdownStyles.toggleButton, style]}
      onPress={onToggle}
      testID={testID}>
      <Text style={dropdownStyles.toggleButtonText}>{value}</Text>
    </TouchableOpacity>
  );
};

const CustomTouchableOpacity = ({
  onPress,
  handleStatusSelect,
  buttonName,
  buttonText,
  orderCount,
  testID,
  onRemove,
}) => {
  const isSelected = Array.isArray(handleStatusSelect) && handleStatusSelect.includes(buttonName);
  return (
    <TouchableOpacity
      onPress={onPress}
      style={dropdownStyles.customTouchable}
      testID={testID}>
      <View style={dropdownStyles.customTouchableContent}>
        <Text style={dropdownStyles.customTouchableCount}>{orderCount}</Text>
        <Text
          style={[
            isSelected && dropdownStyles.dropdownActiveButton,
            dropdownStyles.customTouchableText
          ]}>
          {buttonText || buttonName}
        </Text>
        <TouchableOpacity
          onPress={onRemove}
          style={dropdownStyles.customTouchableReset}
          testID={testID + '_reset'}>
          {isSelected && (
            <i
              className="icon-cancel"
              style={dropdownStyles.customTouchableIcon}></i>
          )}
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

const ShowAllButton = ({onPress, showMore, testID}) => (
  <TouchableOpacity style={dropdownStyles.showAllButton} onPress={onPress} testID={testID}>
    <Text style={dropdownStyles.showAllButtonText}>
      {showMore ? 'Show All' : 'Show Less'}
    </Text>
  </TouchableOpacity>
);

const BaseFilterDropdown = ({
  title,
  subtitle,
  persistentMenuKey,
  includedItems,
  excludedItems,
  clearAllFilters,
  resetFilterCheck,
  status,
  setSelectedItems,
  setFilterIncludedItems,
  matchOptionFlag,
  setMatchOptionFlag,
  showMatchOption = true,
  showAddInput = false,
  onAddItem,
  addInputPlaceholder = 'Add item...',
  maxVisibleItems = 4,
  testIdPrefix = 'filter',
  addedItems = [],
  onRemoveItem,
  fetchSuggestions,
  maxSuggestions = 3
}) => {
  const [isOpen, toggleDropdown] = usePersistentMenuState(persistentMenuKey);
  const [includeOption, setIncludeOption] = useState(true);
  const [matchOption, setMatchOption] = useState(matchOptionFlag === 'All' ? true : false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [addInputValue, setAddInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef('');

  const normalizeStatus = (inputStatus) => {
    if (!inputStatus) return [];
    if (Array.isArray(inputStatus)) {
      if (inputStatus.length > 0 && Array.isArray(inputStatus[0])) {
        return inputStatus[0];
      }
      return inputStatus;
    }
    return [inputStatus];
  };

  const [selectedStatus, setSelectedStatus] = useState(normalizeStatus(status));

  const toggleIncludeOption = () => {
    if (selectedStatus.length > 0) {
      setSelectedStatus([]);
      setSelectedItems([]);
    }

    setIncludeOption(!includeOption);
    setFilterIncludedItems(!includeOption);
  };

  const toggleMatchOption = () => {
    setMatchOption(!matchOption);
    setMatchOptionFlag(!matchOption ? 'All' : 'Any');
  };

  const handleStatusSelect = item => {
    let selectedItems = [...selectedStatus];
    if (!selectedItems.includes(item)) {
      selectedItems.push(item);
    } else {
      selectedItems = selectedItems.filter(i => i !== item);
    }
    setSelectedStatus(selectedItems);
    setSelectedItems(selectedItems);
  };

  const toggleShowAllItems = () => {
    setShowAllItems(prevShowAllItems => !prevShowAllItems);
  };

  const handleAddItem = () => {
    if (addInputValue.trim() && onAddItem) {
      onAddItem(addInputValue.trim());
      setAddInputValue('');
      setSuggestions([]);
    }
  };

  const handleInputChange = useCallback(
    text => {
      setAddInputValue(text);
      if (!fetchSuggestions) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        setSuggestions([]);
        setSuggestionsLoading(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        lastQueryRef.current = trimmed;
        setSuggestionsLoading(true);
        try {
          const list = await fetchSuggestions(trimmed);
          if (lastQueryRef.current === trimmed) {
            setSuggestions(Array.isArray(list) ? list.slice(0, maxSuggestions) : []);
          }
        } catch (_) {
          if (lastQueryRef.current === trimmed) setSuggestions([]);
        } finally {
          if (lastQueryRef.current === trimmed) setSuggestionsLoading(false);
        }
      }, 1000);
    },
    [fetchSuggestions, maxSuggestions]
  );

  const handleSelectSuggestion = useCallback(
    sku => {
      if (onAddItem) onAddItem(sku);
      setAddInputValue('');
      setSuggestions([]);
    },
    [onAddItem]
  );

  useEffect(() => {
    if (clearAllFilters === true) {
      setSelectedStatus([]);
      resetFilterCheck();
    }
  }, [clearAllFilters, resetFilterCheck]);

  useEffect(() => {
    if (status) {
      const normalizedStatus = normalizeStatus(status);
      if (JSON.stringify(normalizedStatus) !== JSON.stringify(selectedStatus)) {
        setSelectedStatus(normalizedStatus);
      }
    }
  }, [status]);

  const baseItems = includeOption ? (includedItems || []) : (excludedItems || []);
  const countProperty = includeOption ? 'order_count' : 'not_present_in_order_count';
  const itemName = item => item?.name ?? item?.sku;
  const addedItemsRows = (addedItems || []).filter(
    name => !baseItems.some(item => (item?.name ?? item?.sku) === name)
  ).map(name => ({
    name,
    text: name,
    [countProperty]: 0
  }));
  const itemsToDisplay = [...baseItems, ...addedItemsRows];

  return (
    <View style={dropdownStyles.dropdownWrapper}>
      <TouchableOpacity style={dropdownStyles.button} onPress={toggleDropdown}>
        <View style={dropdownStyles.buttonContent}>
          <Text style={dropdownStyles.text}>{title}</Text>
          <Image
            style={[dropdownStyles.icon, isOpen && dropdownStyles.iconOpen]}
            source={downArrow}
          />
        </View>
      </TouchableOpacity>
      {isOpen && (
        <View style={[dropdownStyles.toggleContainer, dropdownStyles.toggleContainerInner]}>
          <Text style={dropdownStyles.toggleLabel}>Orders</Text>
          <ToggleButton
            value={includeOption ? i18n.t('filters.include') : i18n.t('filters.exclude')}
            onToggle={toggleIncludeOption}
            style={dropdownStyles.includeToggle}
            testID={`${testIdPrefix}_include_toggle_button`}
          />
          {showMatchOption && (
            <>
              <ToggleButton
                value={matchOption ? i18n.t('filters.all') : i18n.t('filters.any')}
                onToggle={toggleMatchOption}
                style={dropdownStyles.matchToggle}
                testID={`${testIdPrefix}_match_toggle_button`}
              />
              <Text style={dropdownStyles.toggleLabel}> {subtitle?.toLowerCase() || title.toLowerCase()}</Text>
            </>
          )}
        </View>
      )}
      {isOpen && (
        <View style={dropdownStyles.dropdownContent}>
          {showAddInput && (
            <View style={dropdownStyles.addInputWrapper}>
              <View style={dropdownStyles.addInputRow}>
                <View style={dropdownStyles.addInputContainer}>
                  <TextInput
                    style={dropdownStyles.addTextInput}
                    placeholder={addInputPlaceholder}
                    placeholderTextColor="#E7ECF3"
                    value={addInputValue}
                    onChangeText={handleInputChange}
                    onSubmitEditing={handleAddItem}
                    onBlur={() => setTimeout(() => setSuggestions([]), 200)}
                    testID={`${testIdPrefix}_add_input`}
                  />
                  <i className="icon-search" style={dropdownStyles.addInputSearchIcon}></i>
                </View>
                <TouchableOpacity
                  onPress={handleAddItem}
                  style={dropdownStyles.addButton}
                  testID={`${testIdPrefix}_add_button`}>
                  <i className="icon-plus" style={dropdownStyles.addButtonIcon}></i>
                </TouchableOpacity>
              </View>
              {fetchSuggestions && (suggestions.length > 0 || suggestionsLoading) && addInputValue.trim().length > 0 && (
                <View style={dropdownStyles.suggestionsContainer}>
                  {suggestionsLoading && suggestions.length === 0 ? (
                    <Text style={dropdownStyles.suggestionsLoadingText}>Searching...</Text>
                  ) : (
                    suggestions.map(sku => (
                      <TouchableOpacity
                        key={sku}
                        onPress={() => handleSelectSuggestion(sku)}
                        style={dropdownStyles.suggestionRow}
                        testID={`${testIdPrefix}_suggestion_${String(sku).toLowerCase().replace(/\s+/g, '_')}`}>
                        <Text style={dropdownStyles.suggestionText}>{sku}</Text>
                      </TouchableOpacity>
                    ))
                  )}
                </View>
              )}
            </View>
          )}
          {itemsToDisplay
            .slice(0, showAllItems ? itemsToDisplay.length : maxVisibleItems)
            .map(item => {
              const name = itemName(item);
              const isAddedItem = (addedItems || []).includes(name);
              const handleRowPress = () => {
                setFilterIncludedItems(includeOption);
                handleStatusSelect(name);
              };
              const handleCrossPress = isAddedItem && onRemoveItem
                ? () => onRemoveItem(name)
                : handleRowPress;
              return (
                <CustomTouchableOpacity
                  key={name}
                  onPress={handleRowPress}
                  onRemove={handleCrossPress}
                  isAddedItem={isAddedItem}
                  handleStatusSelect={selectedStatus}
                  buttonName={name}
                  buttonText={item.text ?? item.name ?? item.sku}
                  orderCount={item[countProperty] ?? item.order_count ?? 0}
                  testID={`${testIdPrefix}_${String(name).toLowerCase().replace(/\s+/g, '_')}_button`}
                />
              );
            })}

          {itemsToDisplay.length > maxVisibleItems && (
            <ShowAllButton
              onPress={toggleShowAllItems}
              showMore={!showAllItems}
              testID={`${testIdPrefix}_show_all_button`}
            />
          )}
        </View>
      )}
    </View>
  );
};

export default BaseFilterDropdown;
export { ToggleButton, CustomTouchableOpacity, ShowAllButton };
